import { z } from 'zod';
/**
 * Per-user vacation mode state. Persisted at
 * `userPreferences/{orgId}_{userId}_vacationMode` — a single top-level
 * doc whose id is the underscored composite key (NOT a subcollection).
 * The store boundary (`apps/functions/src/firestore/vacation-mode-store.ts`)
 * and the rules layer (`firestore.rules` `match /userPreferences/{prefId}`)
 * both depend on this exact id shape.
 *
 * Per #166 Phase 6 (time & cadence):
 * - `enabled` + (`start`, `end`) define the vacation date-range.
 * - `emergencyCategories` is the operator-editable bypass set —
 *   P0 incidents, payment failures, account security, and system
 *   outages always punch through by default.
 * - `quietHours*` apply year-round (separate from vacation date-range).
 * - `weekendsQuiet` turns every weekend into all-day quiet hours.
 *
 * Times are stored as HH:MM (24h, UTC). Locale-aware rendering is the
 * surface's problem (Phase 5 timezone story still applies).
 */
export const VacationMode = z.object({
    orgId: z.string(),
    userId: z.string(),
    enabled: z.boolean().default(false),
    start: z.string().datetime().optional(),
    end: z.string().datetime().optional(),
    /**
     * P0 incidents, payment failures, account security, system outages
     * bypass the vacation block by default. Operator can edit this set.
     */
    emergencyCategories: z
        .array(z.string())
        .default(['incident.p0', 'billing.payment-failed', 'security.lockout', 'system.outage']),
    /**
     * Quiet hours apply year-round (separate from vacation date-range).
     * Validated as `HH:MM` 24-hour clock — hours `00-23`, minutes `00-59`.
     * Loose `\d{2}:\d{2}` would let `99:99` through and break scheduling.
     */
    quietHoursStart: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .default('19:00'),
    quietHoursEnd: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .default('06:30'),
    /** Whether weekends count as quiet-hours-all-day. */
    weekendsQuiet: z.boolean().default(true),
    updatedAt: z.string().datetime(),
});
